/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { createSlice, nanoid } from "@reduxjs/toolkit";
import { CART_LINE_TYPES, SESSION_STORAGE_CART } from "../../constants/constants";
import { cartRegistry } from "../../utils/cartRegistry";

const hydrateLinesFromSession = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_CART);
    if (!stored) return [];

    const parsed = JSON.parse(stored);

    // Drop stale membership lines from older sessions (now handled by directOrderSlice).
    return Array.isArray(parsed?.lines)
      ? parsed.lines.filter((l) => l?.type !== CART_LINE_TYPES.MEMBERSHIP)
      : [];
  } catch {
    return [];
  }
};

const persistLinesToSession = (lines) => {
  if (!lines.length) {
    sessionStorage.removeItem(SESSION_STORAGE_CART);

    return;
  }

  sessionStorage.setItem(SESSION_STORAGE_CART, JSON.stringify({ lines }));
};

const SESSION_STORAGE_CART_ENTRY_ROUTE = "sb_cart_entry_route";

const hydrateEntryRouteFromSession = () => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_CART_ENTRY_ROUTE) ?? null;
  } catch {
    return null;
  }
};

const persistEntryRouteToSession = (route) => {
  if (!route) {
    sessionStorage.removeItem(SESSION_STORAGE_CART_ENTRY_ROUTE);
    return;
  }
  sessionStorage.setItem(SESSION_STORAGE_CART_ENTRY_ROUTE, route);
};

const findMergeable = (lines, type, params) => {
  const reg = cartRegistry[type];
  if (!reg?.mergeBy) return null;
  const key = reg.mergeBy(params);
  return lines.find((l) => l.type === type && reg.mergeBy(l.params) === key) ?? null;
};

const initialState = {
  lines: hydrateLinesFromSession(),
  invoice: null,
  entryRoute: hydrateEntryRouteFromSession(),
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addLine: {
      reducer: (state, action) => {
        const { clientId, type, snapshot, params } = action.payload;
        // Memberships go through the direct-order lane, never the cart.
        if (type === CART_LINE_TYPES.MEMBERSHIP) {
          return;
        }
        const mergeable = findMergeable(state.lines, type, params);
        if (mergeable) {
          // canEditQty:false types (memberships, packages, gift cards) are
          // one-per-cart. Re-adding does nothing — stops a double-click
          // charging twice.
          if (cartRegistry[type]?.canEditQty === false) {
            return;
          }
          const addQty = params.qty ?? 1;
          const currentQty = mergeable.params.qty ?? 0;
          mergeable.params = { ...mergeable.params, qty: currentQty + addQty };
          mergeable.snapshot = snapshot;
        } else {
          state.lines.push({ clientId, type, snapshot, params });
        }
        persistLinesToSession(state.lines);
      },
      prepare: ({ type, entity, snapshot, params }) => {
        const built = snapshot ?? cartRegistry[type]?.buildSnapshot?.(entity) ?? null;

        return { payload: { clientId: nanoid(), type, snapshot: built, params } };
      },
    },
    updateLineParams: (state, action) => {
      const { clientId, patch } = action.payload;
      const line = state.lines.find((l) => l.clientId === clientId);
      if (!line) return;
      line.params = { ...line.params, ...patch };
      persistLinesToSession(state.lines);
    },
    repriceLine: (state, action) => {
      const { clientId, price } = action.payload;
      const line = state.lines.find((l) => l.clientId === clientId);
      if (!line || !line.snapshot || line.snapshot.price === price) return;
      line.snapshot = { ...line.snapshot, price };
      persistLinesToSession(state.lines);
    },
    removeLine: (state, action) => {
      const { clientId } = action.payload;
      state.lines = state.lines.filter((l) => l.clientId !== clientId);
      persistLinesToSession(state.lines);
    },
    clearLines: (state) => {
      state.lines = [];
      persistLinesToSession(state.lines);
    },
    setInvoice: (state, action) => {
      state.invoice = action.payload;
    },
    setEntryRoute: (state, action) => {
      state.entryRoute = action.payload || null;
      persistEntryRouteToSession(state.entryRoute);
    },
    clearEntryRoute: (state) => {
      state.entryRoute = null;
      persistEntryRouteToSession(null);
    },
    resetCart: () => {
      sessionStorage.removeItem(SESSION_STORAGE_CART);
      sessionStorage.removeItem(SESSION_STORAGE_CART_ENTRY_ROUTE);
      return { lines: [], invoice: null, entryRoute: null };
    },
  },
});

export const {
  addLine,
  updateLineParams,
  repriceLine,
  removeLine,
  clearLines,
  setInvoice,
  resetCart,
  setEntryRoute,
  clearEntryRoute,
} = cartSlice.actions;

export default cartSlice.reducer;
